<script>
  import { onMount, createEventDispatcher } from "svelte";
  import { fly } from "svelte/transition";
  let dispatch = createEventDispatcher();
  import { UserStore, TempUserStore } from "./store.js";
  import { Username, FromPage } from "../account/store.js";
  import * as objectWrapper from "../Utils/ObjectWrapper.js";
  import * as formValidator from "../Utils/ValidateFormFIelds.js";
  import Field from "../FormField.svelte";
  import Email from "../HTMLElements/Email.svelte";
  import Phone from "../HTMLElements/PhoneNumber.svelte";
  import wrappedFetch from "../../wrappedFetch.js";
  import AccountMatchModal from "../account/AccountMatchModal.svelte";


  let showAccountMatchModal = false;
  let recoveredUsername = "";

  let wrappedObj = $TempUserStore.registerContactDetails;
  let emailInput, confirmEmailInput;
  let isFormValid = true;
  let error = "";
  let isValidating;

  let accountExists = false;
  // The matched account is a staff USO account — recovery must go via the
  // LGfL self-service page (identifies staff by date of birth) instead of
  // the parent forgot-username/password flows, which staff cannot pass.
  let staffAccountMatch = false;
  // NB rel="noreferrer" is REQUIRED on this link: the changepass page
  // sniffs the Referrer header and switches into eAdmissions mode
  // (postcode shown, DOB hidden) whenever it sees eadmissions.org.uk.
  const lgflRecoveryUrl =
    "https://support.lgfl.org.uk/public/changepass.aspx?returnto=" +
    encodeURIComponent(window.location.origin + "/signin");
  // Snapshot of the field values at the moment the existing-account panel
  // was shown. Used to detect when the user edits any field afterwards.
  let snapshotAtMatch = "";

  // Combined view of all editable contact fields, so the reactive block has
  // a single dependency to watch.
  $: currentFields = [
    wrappedObj.email.value,
    wrappedObj.confirmEmail.value,
    wrappedObj.Mobile.value,
    wrappedObj.Hometel.value,
    wrappedObj.Worktel.value
  ].join("|");

  // Once the existing-account panel is showing, any change to a field brings
  // the Continue button back.
  $: if (accountExists && currentFields !== snapshotAtMatch) {
    accountExists = false;
    staffAccountMatch = false;
    error = "";
    isFormValid = true;
  }

  onMount(async function () {
    if ($UserStore.registerUser == null)
      dispatch("pageChange", "/register/start");

    emailInput.focus();
    confirmEmailInput.onpaste = e => {
      e.preventDefault();
    };
  });

  async function validateForm(event) {
  event.preventDefault();
  isFormValid = true;
  isValidating = true;
  error = "";
  accountExists = false;

  try {
    if (wrappedObj.confirmEmail.value.toLowerCase() != wrappedObj.email.value.toLowerCase()) {
      error = "Email addresses do not match.";
      isFormValid = false;
      wrappedObj.confirmEmail.isValid = false;
      return;
    }

    // Strip spaces from phone numbers
    ['Mobile', 'Hometel', 'Worktel'].forEach(field => {
      const f = wrappedObj[field];
      if (f?.value?.trim()) f.value = f.value.replace(/\s+/g, '');
    });

    if (!formValidator.validate(wrappedObj)) {
      wrappedObj = wrappedObj;
      error = "Please ensure that you have completed the required fields.";
      isFormValid = false;
      return;
    }

    const postcode = $TempUserStore.registerDetails.postcode.value.replace(
  /(\S*)\s*(\d)/,
  "$1 $2"
);

const userIfExists = await wrappedFetch("user/CheckUserDetailsRegister?email=" +
      wrappedObj.email.value +
      "&mobile=" +
      wrappedObj.Mobile.value)
  .then(res => res.json());
    if (!userIfExists.success) {
      if (userIfExists.recovered) {
        // Archived/recovered account — confirm via masked preview, then verify.
        $Username.value = userIfExists.username;
        FromPage.set("registration");
        recoveredUsername = userIfExists.username;
        showAccountMatchModal = true;
      } else {
        // Active account already matches — show the API message and swap the
        // Continue button for the recovery buttons. Snapshot the current field
        // values so editing any of them restores the Continue button.
        error = userIfExists.message;
        accountExists = true;
        staffAccountMatch = !!userIfExists.isStaff;
        isFormValid = false;
        snapshotAtMatch = currentFields;
      }
      return;
    }

    // const result = await wrappedFetch("uso/IsUsersByMailOrTelExist?email=" +
    //     wrappedObj.email.value +
    //     "&mobileno=" +
    //     wrappedObj.Mobile.value)
    //   .then(res => res.json());

    // if (result.success) {
    //   // An existing account matches — show the API message and swap the
    //   // Continue button for the recovery buttons. Snapshot the current field
    //   // values so editing any of them restores the Continue button.
    //   error = result.message;
    //   accountExists = true;
    //   isFormValid = false;
    //   snapshotAtMatch = currentFields;
    //   return;
    // }

    $UserStore.registerUser.hasCompletedContactDetailsStep = true;
    dispatch("pageChange", "/register/password");
  } catch (e) {
    console.error(e);
    error =  "<strong>There's a problem completing your registration</strong><br><br>" +
  "Something went wrong while setting up your account, so we couldn't complete " +
  "your registration automatically. <br><br>" +
  "Please try again in a few minutes. If the problem continues, contact <a href='/support/step1'>eAdmissions support</a>.";
    isFormValid = false;
  } finally {
    isValidating = false;
  }
}

  function navigateBack() {
    dispatch("pageChange", "/register/details");
  }
</script>

<form action="" class="box" autocomplete="off">
  <div class="field">
    <a href="#" on:click|preventDefault={navigateBack}>
      <span class="fas fa-arrow-left" />
      Back
    </a>
  </div>

  <hr />

  <h1 class="title is-4">Contact Details</h1>
  <p class="subtitle is-6">
    These details will be used for communications from the eAdmissions system.
  </p>

  <Field label="Email address">
    <span slot="inputField">
      <Email
        placeholder="email@domain.com"
        isRequired="true"
        bind:email={wrappedObj.email.value}
        bind:isValid={wrappedObj.email.isValid}
        bind:isDirty={wrappedObj.email.isDirty}
        bind:ref={emailInput}
        label="email address" />
      <!-- bind:ref={emailInput} -->
    </span>
  </Field>

  <Field label="Confirm email address">
    <span slot="inputField">
      <Email
        aria-autocomplete="none"
        placeholder="Re-enter email address"
        isRequired="true"
        bind:email={wrappedObj.confirmEmail.value}
        bind:isValid={wrappedObj.confirmEmail.isValid}
        bind:isDirty={wrappedObj.confirmEmail.isDirty}
        bind:ref={confirmEmailInput}
        bind:emailToCompare={wrappedObj.email.value}
        isForConfirmation={true}
        label="email address" />
    </span>
  </Field>

  <hr />

  <p name="label" class="title is-5" style="margin-top: 30px;">Phone number</p>

  <Field label="Mobile phone number" isRequired={true}>
    <span slot="inputField">
      <Phone
        placeholder="07xxx xxxxxx"
        isRequired="true"
        bind:number={wrappedObj.Mobile.value}
        bind:isValid={wrappedObj.Mobile.isValid}
        bind:isDirty={wrappedObj.Mobile.isDirty}
        isMobile="true"
        isWork="false"
        label="mobile phone number" />
    </span>
  </Field>
  <!-- <p class="help" style="margin-left: 2px;">Please enter your mobile number if you would like to opt into receiving SMS alerts</p> -->

  <Field label="Landline phone number" isRequired={false}>
    <span slot="inputField">
      <Phone
        placeholder="020 1234 5678"
        isRequired="false"
        bind:number={wrappedObj.Hometel.value}
        bind:isValid={wrappedObj.Hometel.isValid}
        bind:isDirty={wrappedObj.Hometel.isDirty}
        isMobile="false"
        isWork="false"
        label="landline phone number" />
    </span>
  </Field>

  <Field label="Work phone number" isRequired={false}>
    <span slot="inputField">
      <Phone
        placeholder="020 9876 5432"
        isRequired="false"
        bind:number={wrappedObj.Worktel.value}
        bind:isValid={wrappedObj.Worktel.isValid}
        bind:isDirty={wrappedObj.Worktel.isDirty}
        isMobile="false"
        isWork="true"
        label="work phone number" />
    </span>
  </Field>

  <hr />

  {#if !isFormValid}
    {#if accountExists}
      <!-- An existing-account match (staff or parent) is guidance, not a
           validation failure — use the same purple panel styling as the
           Forgotten Username page (the master for this message's look)
           instead of the red invalid notification. Genuine validation
           errors (mismatched emails etc.) keep the red styling below. -->
      <div class="notification" style="background:#f5f0f6;">
        <p class="help" style="margin-bottom:0px">{@html error}</p>
      </div>
    {:else}
      <div class="notification is-invalid">
        <p class="help" style="margin-bottom:0px">{@html error}</p>
      </div>
    {/if}
  {/if}

  {#if accountExists}
  <div class="buttons is-flex is-flex-direction-column">
    <button
      class="button is-purple is-fullwidth"
      on:click|preventDefault={() => dispatch("pageChange", "/account/login")}>
      Sign in
    </button>
    {#if staffAccountMatch}
      <!-- Staff accounts: the parent forgot-username flow needs a home
           postcode (staff have none) and the forgot-password flow needs a
           usable email — the LGfL self-service page handles both for staff,
           identifying them by date of birth. Opens in a new tab; its final
           step links back to our sign-in via returnto. A real <button> so it
           styles identically to Sign in; window.open with "noreferrer"
           omits the Referer header, which is REQUIRED — the changepass page
           switches to postcode/eAdmissions mode when it sees our referrer. -->
      <!-- white-space/height overrides: Bulma buttons are nowrap with a
           fixed height, so a label longer than the form column overflows
           the frame instead of wrapping. -->
      <button
        class="button is-fullwidth"
        style="white-space:normal; height:auto;"
        on:click|preventDefault={() => window.open(lgflRecoveryUrl, "_blank", "noopener,noreferrer")}>
        Forgotten Username/Password
      </button>
    {:else}
      <button
        class="button is-fullwidth"
        on:click|preventDefault={() => dispatch("pageChange", "/account/forgotusername")}>
        Forgot username
      </button>
      <button
        class="button is-fullwidth"
        on:click|preventDefault={() => dispatch("pageChange", "/account/forgotpassword")}>
        Forgot password
      </button>
    {/if}
  </div>
{:else}
    <div style="text-align: right;">
      <button
        type="submit"
        class="button is-purple {isValidating ? 'is-loading' : ''}"
        on:click={validateForm}
        disabled={isValidating}>
        Continue
      </button>
    </div>
  {/if}
</form>


<AccountMatchModal
  bind:showModal={showAccountMatchModal}
  username={recoveredUsername}
  declineLabel="Cancel"
  closeOnDecline={true}
  introText="We've found an inactive account that matches the details you provided. It was archived after more than 3 years of inactivity, in line with our data policy. Check the masked details below — if they look like yours, we'll send a verification code to your email to confirm it's you before reinstating the account."
  on:proceed={() => dispatch("pageChange", "/account/verifyidentity")}
/>